/**
 * Liste virtualisée du roadbook — EF-RBK-007, ENF-018.
 *
 * Seules les lignes visibles sont rendues. La hauteur des lignes n'est pas
 * uniforme — les lignes d'étape et les groupes diffèrent des lignes de POI —
 * donc les hauteurs sont mesurées et mises en cache, avec une estimation par
 * défaut pour les lignes non encore rendues.
 */

import type { JSX } from 'preact';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'preact/hooks';
import type { CategoryKey } from '../../core/categories';
import type { LocalDateTime } from '../../core/local-datetime';
import type { Poi, PoiId, RoadbookDocument } from '../../core/model';
import type { DisplayRow } from '../../roadbook/display';
import type { PoiLineRef } from '../../roadbook/order';
import { expandedRows, roadbookScrollTop, toggleRowExpanded } from '../state';
import { RoadbookRow, type RaceRowView } from './RoadbookRow';

/** Nombre d'icônes tenant sur un rang, pour la seule estimation de hauteur. */
const ICONS_PER_ROW = 6;

/**
 * Hauteur estimée d'une ligne, en pixels CSS, avant mesure. Un groupe affiche
 * toutes ses icônes, sur plusieurs rangs si nécessaire, et porte ses valeurs
 * en dessous : sa hauteur dépend donc du nombre de membres.
 */
function estimatedHeight(row: DisplayRow | undefined, expanded: boolean): number {
  if (row === undefined) return 64;
  // Un dépliant ouvert est mesuré dès le premier rendu ; l'estimation évite
  // seulement un saut brutal de la barre de défilement en attendant.
  const detail = expanded ? 420 : 0;
  switch (row.kind) {
    case 'stop':
      return 76;
    case 'missingRange':
      return 104;
    case 'group':
      return 40 + Math.ceil(row.members.length / ICONS_PER_ROW) * 52 + detail;
  }
}

/**
 * Clé stable d'une ligne, indépendante de son indice : les filtres déplacent
 * les indices, pas l'identité des lignes.
 */
export function rowKey(row: DisplayRow, index: number): string {
  if (row.kind === 'group') return `g-${row.anchor.poiId}-${row.anchor.passageIndex}`;
  if (row.kind === 'stop') return `s-${row.stop.type}-${index}`;
  return `m-${row.range.fromProgressionMeters}`;
}

/** Nombre de lignes rendues au-delà de la fenêtre visible. */
const OVERSCAN = 4;

export interface RoadbookListProps {
  readonly rows: readonly DisplayRow[];
  readonly document: RoadbookDocument;
  readonly pois: ReadonlyMap<PoiId, Poi>;
  readonly hiddenCategories: ReadonlySet<CategoryKey>;
  readonly routeLengthMeters: number;
  readonly elevationAvailable: boolean;
  readonly totalGainMeters: number | null;
  readonly startLocal: LocalDateTime | null;
  readonly highlightedIndex: number | null;
  readonly emphasizedLine: PoiLineRef | null;
  readonly scrollToIndex: number | null;
  /**
   * Change de valeur à chaque nouvelle demande de positionnement. Il permet de
   * redemander la même ligne après que l'utilisateur a fait défiler la liste.
   */
  readonly scrollRequestId: number;
  readonly onSearchZone: ((from: number, to: number) => void) | null;
  readonly searchZoneDisabledReason: string | null;
  /** Non nul en mode course, dès qu'une position a été retenue. */
  readonly raceView: RaceRowView | null;
  readonly height: string;
  /**
   * Contenu placé au-dessus des lignes, **dans la même zone défilante** :
   * synthèse, marqueurs, états vides. EF-NAV-006 n'admet qu'un seul
   * défilement sur cet écran ; un en-tête posé au-dessus de la liste en
   * créerait un second.
   */
  readonly header?: JSX.Element | null;
  /**
   * Restitue la position de défilement à l'ouverture et la publie à chaque
   * défilement : l'onglet Roadbook conserve sa place (EF-NAV-007).
   */
  readonly preserveScroll?: boolean;
}

export function RoadbookList(props: RoadbookListProps): JSX.Element {
  const container = useRef<HTMLDivElement>(null);
  const heights = useRef<Map<number, number>>(new Map());
  const [scrollTop, setScrollTop] = useState(0);
  const [viewport, setViewport] = useState(600);
  const [measureTick, setMeasureTick] = useState(0);
  // Hauteur de l'en-tête, mesurée : elle décale l'origine des lignes dans la
  // zone défilante commune.
  const headerBox = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  // Demande de positionnement en cours. Les hauteurs de ligne n'étant pas
  // connues avant le premier rendu, la demande reste vivante et se réapplique
  // à chaque affinement des offsets, jusqu'à ce que l'utilisateur fasse
  // défiler la liste lui-même.
  const requestedIndex = useRef<number | null>(null);
  const appliedOffset = useRef<number | null>(null);
  const appliedTop = useRef(0);

  // Somme préfixe des hauteurs, recalculée seulement lorsqu'une mesure change.
  const expanded = expandedRows.value;
  const offsets = useMemo(() => {
    const prefix = new Float64Array(props.rows.length + 1);
    for (let i = 0; i < props.rows.length; i += 1) {
      const row = props.rows[i];
      const fallback = estimatedHeight(row, row !== undefined && expanded.has(rowKey(row, i)));
      prefix[i + 1] = (prefix[i] ?? 0) + (heights.current.get(i) ?? fallback);
    }
    return prefix;
  }, [props.rows, measureTick, expanded]);

  const totalHeight = offsets[props.rows.length] ?? 0;

  useEffect(() => {
    const element = container.current;
    if (element === null) return;
    const observer = new ResizeObserver(() => {
      setViewport(element.clientHeight);
    });
    observer.observe(element);
    setViewport(element.clientHeight);
    // La liste retrouve la position où l'utilisateur l'avait laissée.
    if (props.preserveScroll === true && roadbookScrollTop.peek() > 0) {
      element.scrollTop = roadbookScrollTop.peek();
      appliedTop.current = element.scrollTop;
      setScrollTop(element.scrollTop);
    }
    return () => {
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // « Aller au kilomètre », retour depuis le détail d'un POI, POI mis en
  // évidence en mode course : toute demande de positionnement passe par ici.
  useEffect(() => {
    requestedIndex.current = props.scrollToIndex;
    appliedOffset.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.scrollToIndex, props.scrollRequestId]);

  useLayoutEffect(() => {
    const box = headerBox.current;
    if (box === null) return;
    const height = box.offsetHeight;
    if (height !== headerHeight) setHeaderHeight(height);
  });

  useLayoutEffect(() => {
    const target = requestedIndex.current;
    const element = container.current;
    if (target === null || element === null) return;
    const offset = (offsets[target] ?? 0) + headerHeight;
    // Réappliquer n'a de sens que si une mesure a déplacé la ligne visée.
    if (appliedOffset.current !== null && Math.abs(appliedOffset.current - offset) < 1) return;
    appliedOffset.current = offset;
    element.scrollTop = offset;
    appliedTop.current = element.scrollTop;
    setScrollTop(element.scrollTop);
  });

  // Position de défilement rapportée à l'origine des lignes.
  const listTop = scrollTop - headerHeight;
  const start = Math.max(0, findIndexAtOffset(offsets, listTop, props.rows.length) - OVERSCAN);
  const end = Math.min(
    props.rows.length,
    findIndexAtOffset(offsets, listTop + viewport, props.rows.length) + OVERSCAN + 1,
  );

  const measured = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const wrapper = measured.current;
    if (wrapper === null) return;
    let changed = false;
    for (const child of Array.from(wrapper.children)) {
      const index = Number((child as HTMLElement).dataset['index']);
      if (!Number.isFinite(index)) continue;
      const height = (child as HTMLElement).offsetHeight;
      if (height > 0 && heights.current.get(index) !== height) {
        heights.current.set(index, height);
        changed = true;
      }
    }
    if (changed) setMeasureTick((tick) => tick + 1);
  });

  return (
    <div
      class="roadbook-list"
      ref={container}
      style={`height:${props.height};`}
      onScroll={(event) => {
        const top = (event.currentTarget as HTMLDivElement).scrollTop;
        // Un défilement qui n'est pas le nôtre abandonne la demande en cours :
        // l'utilisateur reprend la main sur la position de la liste.
        if (Math.abs(top - appliedTop.current) > 1) requestedIndex.current = null;
        if (props.preserveScroll === true) roadbookScrollTop.value = top;
        setScrollTop(top);
      }}
      aria-label="Roadbook"
    >
      <div ref={headerBox}>{props.header}</div>
      <div role="list" style={`height:${totalHeight}px; position:relative;`}>
        <div ref={measured} style={`position:absolute; top:${offsets[start] ?? 0}px; left:0; right:0;`}>
          {props.rows.slice(start, end).map((row, offset) => {
            const index = start + offset;
            const key = rowKey(row, index);
            return (
              <div key={key} data-index={index} role="listitem">
                <RoadbookRow
                  row={row}
                  document={props.document}
                  pois={props.pois}
                  hiddenCategories={props.hiddenCategories}
                  routeLengthMeters={props.routeLengthMeters}
                  elevationAvailable={props.elevationAvailable}
                  totalGainMeters={props.totalGainMeters}
                  startLocal={props.startLocal}
                  highlighted={props.highlightedIndex === index}
                  emphasizedLine={props.highlightedIndex === index ? props.emphasizedLine : null}
                  expanded={expanded.has(key)}
                  onToggleDetail={() => {
                    toggleRowExpanded(key);
                  }}
                  onSearchZone={props.onSearchZone}
                  searchZoneDisabledReason={props.searchZoneDisabledReason}
                  raceView={props.raceView}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/** Dernier indice dont l'offset est inférieur ou égal à la position donnée. */
function findIndexAtOffset(offsets: Float64Array, position: number, count: number): number {
  let low = 0;
  let high = count;
  while (low < high) {
    const middle = (low + high) >> 1;
    if ((offsets[middle] ?? 0) <= position) low = middle + 1;
    else high = middle;
  }
  return Math.max(0, low - 1);
}
